import { ReactElement } from 'react';

import { Box, Button, ButtonBase } from '@mui/material';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';

import TextField from '@/components/TextField';
import Typography from '@/components/Typography';
import { responsiveFontSizes } from '@/components/Typography/fontSizes';
import { spacing } from '@/constants/ui';
import { ErrorMessage } from '@/pages/Onboarding/components/ErrorMessage';
import { ThanksMessage } from '@/pages/Onboarding/components/ThanksMessage';
import useLoginComponent from '@/pages/Onboarding/LoginComponent/useLoginComponent';

const LoginComponent = ({
  emailButtonLabel = 'Login',
  loginHeader,
  loginFooter
}: {
  emailButtonLabel?: string;
  loginHeader?: ReactElement;
  loginFooter?: ReactElement;
}) => {
  const {
    handleChangeEmail,
    handleOnKeyDown,
    status,
    error,
    handleLogin,
    email,
    refreshReCaptcha,
    onVerify,
    captchaToken,
    isLoggedIn,
    resetPage
  } = useLoginComponent();

  const loading = status === 'loading';

  const renderContent = () => {
    if (status === 'fulfilled') {
      if (isLoggedIn) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing['2'],
              mb: spacing['2']
            }}
          >
            <Typography size={24}>You've been logged in.</Typography>
            <Typography size={24}>You can now close this window.</Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing['2'],
            mb: spacing['2']
          }}
        >
          <Typography size={32}>We've sent a link to your email address</Typography>
          <Typography
            size={32}
            sx={{ fontWeight: 700 }}
          >
            {email}
          </Typography>
          <Typography size={32}>Please check your inbox.</Typography>
          <Typography size={20}>
            If you don't see the email, please check your spam folder or&nbsp;
            <ButtonBase
              sx={{ color: 'primary.main', fontSize: responsiveFontSizes['20'], mb: '3px' }}
              onClick={resetPage}
            >
              try resending
            </ButtonBase>
            .
          </Typography>
        </Box>
      );
    }
    if (status === 'notFound') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing['2'],
            mb: spacing['2']
          }}
        >
          <Typography
            size={32}
            sx={{ fontWeight: 'bold' }}
          >
            First, let’s verify your email
          </Typography>
          <Typography
            size={20}
            sx={{ fontWeight: 500 }}
          >
            Check {email} to verify your account and get started
          </Typography>
        </Box>
      );
    }
    if ('onSuppressionList' === status) return <ThanksMessage />;
    if ('failed' === status) return <ErrorMessage />;

    return (
      <Box>
        {loginHeader}
        <Box
          minWidth={{
            xs: '100%',
            md: '360px'
          }}
        >
          <TextField
            autoFocus
            fullWidth
            autoComplete='email'
            disabled={loading}
            error={!!error}
            helperText={error}
            id='email'
            label='Email'
            name='email'
            type='email'
            value={email}
            variant='filled'
            onChange={handleChangeEmail}
            onKeyDown={handleOnKeyDown}
          />
        </Box>
        <Button
          fullWidth
          disabled={loading || !captchaToken}
          sx={{
            height: {
              xs: 36,
              md: 48
            },
            mt: 1,
            mb: 2,
            color: 'text.primary.main',
            fontWeight: 600,
            fontSize: 16
          }}
          variant='contained'
          onClick={handleLogin}
        >
          {loading ? 'Logging in...' : emailButtonLabel}
        </Button>
        {loginFooter}
      </Box>
    );
  };

  return (
    <>
      <GoogleReCaptcha
        action='login'
        refreshReCaptcha={refreshReCaptcha}
        onVerify={onVerify}
      />
      {renderContent()}
    </>
  );
};
export default LoginComponent;
